<template>
  <main class="invest-page">
    <section class="invest-panel" aria-label="Request a blockchain investigation">
      <header class="panel__head">
        <div class="head__text">
          <h2 class="title">Request a blockchain investigation</h2>
          <p class="subtitle" v-if="fromCasePrep">
            This page helps you copy your Case Prep Buddy transaction data before opening the
            <strong>CoinStructive.com</strong> investigation intake form. The form opens in a new tab and is
            not on PathTracer, so your transaction data cannot be sent into it automatically.
          </p>
          <p class="subtitle" v-else>
            This will open a short intake form on <strong>CoinStructive.com</strong> in a new tab.
            Use it when you need a certified human review and a polished report.
          </p>
        </div>

        <div class="head__art" aria-hidden="true">
          <img class="hero-img" :src="heroImage" alt="" />
        </div>
      </header>

      <div class="panel__body">
        <div v-if="loadError" class="error" style="margin-bottom: 12px;">
          {{ loadError }}
        </div>

        <section
          v-if="fromCasePrep"
          class="notice notice--warning"
          role="note"
          aria-label="Copy your transaction data before continuing"
        >
          <strong>Copy your transaction data before opening the form</strong>
          <p class="notice__text">
            You are about to leave PathTracer for a separate CoinStructive intake form. Copy the TSV or the
            hash + To Address list below and keep it somewhere handy so you can paste it into the form.
          </p>
        </section>

        <section v-else class="notice notice--info" role="note" aria-label="Budget-friendly notice">
          <strong>Note for clients referred to PathTracer</strong>
          <p class="notice__text">
            If our investigations team directed you here as a budget-friendly DIY option, you can ignore the intake form below.
            Only continue if you want to request a full manual investigation.
          </p>
        </section>

        <section v-if="clipboardMessage" class="notice notice--success" role="status" aria-live="polite">
          <strong>{{ clipboardMessage }}</strong>
          <p class="notice__text">Now you can paste it into your notes or into the external intake form.</p>
        </section>

        <section class="meta-card">
          <div class="meta-grid">
            <div class="meta-row" v-if="requestId">
              <span class="label">Request ID</span>
              <span class="value mono">{{ requestId }}</span>
            </div>

            <div class="meta-row" v-if="casePrepRunTitle">
              <span class="label">Prep run</span>
              <span class="value">{{ casePrepRunTitle }}</span>
            </div>

            <div class="meta-row" v-if="casePrepRunId">
              <span class="label">Run ID</span>
              <span class="value mono truncate" :title="casePrepRunId">{{ casePrepRunId }}</span>
            </div>

            <div class="meta-row" v-if="fromCasePrep">
              <span class="label">Prepared rows</span>
              <span class="value">{{ casePrepTransactionCount }} transaction<span v-if="casePrepTransactionCount !== 1">s</span></span>
            </div>

            <div class="meta-row" v-if="tx">
              <span class="label">Transaction</span>
              <span class="value mono truncate" :title="tx">{{ tx }}</span>
            </div>

            <div class="meta-row" v-if="to">
              <span class="label">To</span>
              <span class="value mono truncate" :title="to">{{ to }}</span>
            </div>
          </div>
        </section>

        <section v-if="fromCasePrep" class="case-prep-copy-card">
          <div class="case-prep-copy-card__head">
            <div>
              <p class="eyebrow">From Case Prep Buddy</p>
              <h3>Transaction data to paste into the intake form</h3>
            </div>
            <div class="case-prep-copy-card__actions">
              <button
                class="btn btn--outline"
                type="button"
                :disabled="!casePrepTsv"
                @click="copyToClipboard(casePrepTsv, 'TSV')"
              >
                Copy TSV
              </button>
              <button
                class="btn btn--outline"
                type="button"
                :disabled="!casePrepHashAndToAddressList"
                @click="copyToClipboard(casePrepHashAndToAddressList, 'hash + To Address list')"
              >
                Copy hash + To Address list
              </button>
            </div>
          </div>

          <p class="note">
            The TSV keeps transaction hashes in Column B and To Address values in Column D. That shape is still useful
            for your own records and for future Case Buddy handoff, but the external investigation form will need you
            to paste the information manually.
          </p>

          <textarea
            v-if="casePrepTsv"
            class="copy-textarea"
            :value="casePrepTsv"
            rows="8"
            readonly
            aria-label="Case Prep Buddy TSV transaction data"
          ></textarea>

          <p v-else class="empty-state">
            I could not load the prepared transaction data for this tab. Go back to Case Prep Buddy, copy the TSV from
            the final sheet, then continue to the external intake form.
          </p>
        </section>

        <section class="callout">
          <strong>When manual investigation helps</strong>
          <ul class="bullets">
            <li><strong>Certification:</strong> confirm the automated trace and sanity-check edge cases.</li>
            <li><strong>Complex flows:</strong> smart contracts, bridges, memos/tags, and emerging services.</li>
            <li><strong>High-stakes outcomes:</strong> disputes, compliance, legal, or internal documentation.</li>
          </ul>

          <p class="note">
            PathTracer is excellent for speed and scale. Human review is best when you want added certainty,
            context, and your strongest report.
          </p>
        </section>
      </div>

      <footer class="panel__footer">
        <button class="btn btn--subtle" type="button" @click="goBack">
          Back
        </button>

        <a
          class="btn btn--cta"
          :href="intakeUrl"
          target="_blank"
          rel="noopener noreferrer"
          data-track="investigation_continue_intake"
          :data-track-props="JSON.stringify({ id: requestId, source: fromCasePrep ? 'case-prep' : 'direct' })"
        >
          Open external intake form
        </a>
      </footer>
    </section>
  </main>
</template>

<script>
import heroPng from '@/assets/investigation-report.png';

const CASE_PREP_INVESTIGATION_STORAGE_KEY = 'pt:casePrepInvestigationRequest';

export default {
  name: 'RequestInvestigation',

  data() {
    return {
      loadError: '',
      clipboardMessage: '',
      casePrepPayload: null,
      externalBaseUrl: 'https://coinstructive.com/investigations-intake/',
      heroImage: heroPng,
    };
  },

  mounted() {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    this.loadCasePrepPayload();
  },

  computed: {
    requestId() {
      const routeParamId = this.$route?.params?.requestId
        ? String(this.$route.params.requestId)
        : '';
      const routeQueryId = this.$route?.query?.requestId
        ? String(this.$route.query.requestId)
        : '';

      return routeParamId || routeQueryId || String(this.casePrepPayload?.id || '');
    },

    fromCasePrep() {
      const routeSource = String(this.$route?.query?.source || '').toLowerCase();
      if (routeSource === 'case-prep') return true;

      const routeId = this.requestId;

      return Boolean(
        routeId
        && this.casePrepPayload?.source === 'case-prep'
        && routeId === this.casePrepPayload.id
      );
    },

    tx() {
      return this.$route?.query?.tx ? String(this.$route.query.tx) : '';
    },

    to() {
      return this.$route?.query?.to ? String(this.$route.query.to) : '';
    },

    mode() {
      return this.$route?.query?.mode ? String(this.$route.query.mode) : '';
    },

    modeLabel() {
      if (!this.mode) return '';
      const m = this.mode.toLowerCase();
      if (m === 'advanced') return 'Advanced';
      if (m === 'normal') return 'Normal';
      return this.mode;
    },

    casePrepTsv() {
      return String(this.casePrepPayload?.tsv || '');
    },

    casePrepHashAndToAddressList() {
      return String(this.casePrepPayload?.hashAndToAddressList || '');
    },

    casePrepTransactionCount() {
      const fromPayload = Number(this.casePrepPayload?.transactionCount || 0);
      if (fromPayload) return fromPayload;
      if (Array.isArray(this.casePrepPayload?.transactions)) return this.casePrepPayload.transactions.length;
      const fromQuery = Number(this.$route?.query?.txCount || 0);
      return Number.isFinite(fromQuery) ? fromQuery : 0;
    },

    casePrepRunTitle() {
      return String(this.casePrepPayload?.prepRunTitle || '').trim();
    },

    casePrepRunId() {
      return String(this.casePrepPayload?.prepRunId || '').trim();
    },

    intakeUrl() {
      return `${this.externalBaseUrl}`;
    },
  },

  methods: {
    loadCasePrepPayload() {
      try {
        const raw = window.sessionStorage.getItem(CASE_PREP_INVESTIGATION_STORAGE_KEY);
        if (!raw) return;

        const payload = JSON.parse(raw);
        if (!payload || payload.source !== 'case-prep') return;

        this.casePrepPayload = payload;
      } catch (error) {
        console.debug('RequestInvestigation could not load Case Prep Buddy payload', error);
        this.loadError = 'Could not load the Case Prep Buddy transaction data for this tab.';
      }
    },

    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ path: '/' });
    },

    async copyToClipboard(text, label = 'data') {
      const value = String(text || '');
      if (!value) {
        this.loadError = `There is no ${label} to copy.`;
        return;
      }

      try {
        await navigator.clipboard.writeText(value);
        this.loadError = '';
        this.clipboardMessage = `${label} copied`;
      } catch (e) {
        console.debug('RequestInvestigation copy failed', e);
        this.loadError = 'Unable to copy to clipboard in this browser context.';
      }
    },
  },
};
</script>

<style scoped>
.invest-page {
  --bg: var(--bg, #f7f9fc);
  --card: var(--card, #ffffff);
  --ink: var(--ink, #1f2a37);
  --muted: var(--muted, #6b7280);
  --border: var(--border, #e5e8ec);
  --accent: var(--accent, #35495e);

  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  padding: 24px 16px;
  display: grid;
  place-items: start center;
}

.invest-panel {
  width: min(880px, 100%);
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 16px 34px rgba(16, 24, 40, 0.10);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel__head {
  padding: 16px 18px;
  background:
    radial-gradient(220px 140px at 90% 15%, rgba(44, 128, 180, .12), transparent 60%),
    linear-gradient(180deg, #ffffff, #f6f8fd);
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: center;
}

.title {
  margin: 0;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
}

.subtitle {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: .95rem;
  max-width: 70ch;
  line-height: 1.5;
}

.head__art {
  width: 200px;
  max-width: 200px;
  padding: 6px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(15, 38, 61, 0.10);
}

.hero-img {
  width: 200px;
  height: 122px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid rgba(15, 38, 61, 0.14);
  box-shadow: 0 12px 26px rgba(16, 24, 40, 0.14);
  background: #0e1c2d;
}

.panel__body {
  padding: 16px 18px 18px;
  display: grid;
  gap: 12px;
}

.error {
  color: #8f1d1d;
  font-weight: 700;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  background: #f2f8fe;
  border-color: #cfe0ec;
}

.notice strong {
  display: block;
  margin-bottom: 4px;
  color: #0f2740;
}

.notice--warning {
  background: #fffbeb;
  border-color: #f6d365;
}

.notice--warning strong {
  color: #7a4b00;
}

.notice--success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.notice--success strong {
  color: #166534;
}

.notice__text {
  margin: 0;
  color: var(--muted);
  font-size: 0.92rem;
  line-height: 1.45;
}

.meta-card,
.callout,
.case-prep-copy-card {
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.meta-grid {
  display: grid;
  gap: 8px;
}

.meta-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 10px;
  align-items: center;
}

.label {
  color: var(--muted);
  font-size: .9rem;
}

.value {
  font-weight: 700;
  min-width: 0;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
}

.truncate {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
}

.case-prep-copy-card {
  display: grid;
  gap: 12px;
  background: linear-gradient(180deg, #ffffff, #fffbeb);
  border-color: rgba(214, 161, 10, 0.38);
}

.case-prep-copy-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.case-prep-copy-card__head h3 {
  margin: 0;
}

.case-prep-copy-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.eyebrow {
  margin: 0 0 4px;
  color: #475569;
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.copy-textarea {
  width: 100%;
  min-height: 180px;
  resize: vertical;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  font: .88rem ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  color: #0f172a;
  background: #fff;
}

.empty-state {
  margin: 0;
  padding: 10px;
  border-radius: 10px;
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.bullets {
  margin: 10px 0 0 18px;
  padding: 0;
  color: var(--ink);
  font-size: .94rem;
  line-height: 1.45;
}

.note {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: .86rem;
  line-height: 1.45;
}

.panel__footer {
  border-top: 1px solid var(--border);
  padding: 14px 18px;
  background: #fff;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: .55rem .85rem;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--accent);
  text-decoration: none;
  transition: background 140ms ease, box-shadow 140ms ease, transform 140ms ease;
}

.btn:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.btn:active {
  transform: translateY(1px);
}

.btn--subtle {
  background: #f3f6fb;
  border-color: #d9e2eb;
  color: var(--accent);
}

.btn--subtle:hover {
  background: #eaf0f7;
}

.btn--outline {
  background: transparent;
  border-color: #d0d7de;
  color: var(--ink);
}

.btn--outline:hover:not(:disabled) {
  background: #f6f8fa;
}

.btn--cta {
  margin-left: auto;
  border-color: #d6a10a;
  background: linear-gradient(180deg, #ffe07a, #f3b61a);
  color: #111827;
  box-shadow: 0 10px 18px rgba(243, 182, 26, 0.22);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
}

.btn--cta:hover {
  background: linear-gradient(180deg, #ffe89a, #f6c233);
  box-shadow: 0 12px 22px rgba(243, 182, 26, 0.26);
}

.btn--cta:focus {
  outline: none;
  box-shadow:
    0 0 0 3px rgba(243, 182, 26, 0.35),
    0 10px 18px rgba(243, 182, 26, 0.22);
}

@media (max-width: 720px) {
  .panel__head,
  .meta-row {
    grid-template-columns: 1fr;
  }

  .head__art {
    width: 100%;
    max-width: none;
  }

  .hero-img {
    width: 100%;
    height: 150px;
  }

  .case-prep-copy-card__head {
    flex-direction: column;
  }

  .case-prep-copy-card__actions,
  .btn--cta {
    width: 100%;
  }

  .btn {
    width: 100%;
  }

  .btn--cta {
    margin-left: 0;
  }
}
</style>
